import {useState} from 'react';
import {confirm} from '../../../components/Confirm';
import {voucherCode, VoucherLike} from '../utils/vouchers';
import {useToast} from 'components/ui/chakra';
import VoucherController from 'puddleglum/api/providers/VoucherController';

interface UseVoucherActionsOptions {
	onChange: () => Promise<unknown>;
}

const useVoucherActions = ({onChange}: UseVoucherActionsOptions) => {
	const toast = useToast();
	const [isRedeemingId, setIsRedeemingId] = useState<number | null>(null);
	const [isCancelingId, setIsCancelingId] = useState<number | null>(null);

	const redeemVoucher = async (voucher: VoucherLike) => {
		setIsRedeemingId(voucher.id);
		try {
			await VoucherController.redeem(voucher.id, {});
			await onChange();
			toast({
				title: 'Voucher redeemed',
				description: `${voucherCode(voucher)} was marked as redeemed.`,
				status: 'success',
				duration: 5000,
				isClosable: true,
			});
		} catch (error: any) {
			toast({
				title: 'Unable to redeem voucher',
				description:
					error?.response?.data?.message ||
					'This voucher could not be redeemed. Please refresh and try again.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		} finally {
			setIsRedeemingId(null);
		}
	};

	const cancelVoucher = (voucher: VoucherLike) => {
		confirm({
			title: 'Cancel this voucher?',
			content: 'This will mark the voucher as canceled. It will no longer show as active.',
			onOk: async () => {
				setIsCancelingId(voucher.id);
				try {
					await VoucherController.cancel(voucher.id);
					await onChange();
					toast({
						title: 'Voucher canceled',
						description: `${voucherCode(voucher)} was marked as canceled.`,
						status: 'success',
						duration: 5000,
						isClosable: true,
					});
				} catch (error: any) {
					toast({
						title: 'Unable to cancel voucher',
						description:
							error?.response?.data?.message ||
							'This voucher could not be canceled. Please refresh and try again.',
						status: 'error',
						duration: 5000,
						isClosable: true,
					});
				} finally {
					setIsCancelingId(null);
				}
			},
		});
	};

	return {redeemVoucher, cancelVoucher, isRedeemingId, isCancelingId};
};

export default useVoucherActions;
